<template>
  <!-- 适配第二种深色卡片风格 -->
  <div class="game-stats-container">
    <h2 class="stats-title">游戏数据统计面板</h2>

    <div class="date-info" v-if="props.selectedDate">
      <span class="date-label">当前查询日期：</span>
      <span class="date-value">{{ props.selectedDate }}</span>
    </div>

    <!-- 玩家姓名输入框（替代原NPC选择器） -->
    <div class="player-input-wrapper">
      <label class="input-label">玩家名称：</label>
      <input 
        type="text" 
        v-model="playerName" 
        class="player-input"
        placeholder="请输入玩家名称"
        @keyup.enter="fetchDropStats"
      >
    </div>

    <!-- 充值记录搜索框 -->
    <div v-if="activeTab === 'charge'" class="search-wrapper">
      <input 
        type="text" 
        v-model="chargeKeyword"
        @keyup.enter="loadChargeRecords"
        placeholder="输入玩家名称搜索..." 
        class="search-input"
      >
      <i class="fa fa-search search-icon"></i>
    </div>

    <!-- 标签切换 -->
    <div class="tab-switcher">
      <button 
        class="tab-btn" 
        :class="{ active: activeTab === 'drop' }"
        @click="switchTab('drop')"
      >
        掉落数据
      </button>
      <button 
        class="tab-btn" 
        :class="{ active: activeTab === 'charge' }"
        @click="switchTab('charge')"
      >
        充值记录
      </button>
    </div>

    <!-- 掉落数据区域 -->
    <div v-if="activeTab === 'drop'" class="data-panel">
      <!-- 加载中 -->
      <div v-if="loading" class="loading-tip">
        <i class="fa fa-spinner fa-spin mr-2"></i>
        <span>加载掉落数据中...</span>
      </div>
      <!-- 错误提示 -->
      <div v-else-if="error" class="error-tip">
        <i class="fa fa-exclamation-circle mr-1"></i>
        <span>获取掉落数据失败，请重试</span>
      </div>
      <!-- 无数据 -->
      <div v-else-if="groupedDropStats.length === 0 && !loading" class="empty-tip">
        暂无掉落数据
      </div>
      <!-- 掉落数据表格（已支持相同物品折叠） -->
      <table v-else class="stats-table">
        <thead>
          <tr>
            <th>物品名称</th>
            <th>来源</th>
            <th>稀有度</th>
            <th>类型</th>
            <th>数量</th>
            <th>获取时间</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="group in groupedDropStats" :key="group.itemName">
            <!-- 分组折叠行 -->
            <tr class="group-row" @click="toggleExpand(group.itemName)">
              <td class="expand-cell">
                <i 
                  class="fa" 
                  :class="expandedItems.has(group.itemName) ? 'fa-chevron-down' : 'fa-chevron-right'"
                ></i>
                {{ group.itemName }}
              </td>
              <td>--</td>
              <td>--</td>
              <td>--</td>
              <td>{{ group.totalCount }}</td>
              <td>--</td>
            </tr>
            <!-- 展开子项 -->
            <template v-if="expandedItems.has(group.itemName)">
              <tr v-for="item in group.items" :key="item.id" class="item-row">
                <td class="indent-cell">{{ item.itemName || '未知物品' }}</td>
                <td>{{ item.sources || '未知来源' }}</td>
                <td>{{ item.rarity || '普通' }}</td>
                <td>{{ item.type || '未知类型' }}</td>
                <td>{{ item.count || 0 }}</td>
                <td>{{ item.timestamp || new Date().toLocaleString() }}</td>
              </tr>
            </template>
          </template>
        </tbody>
      </table>
    </div>

    <!-- 充值记录区域 -->
    <div v-if="activeTab === 'charge'" class="data-panel">
      <div v-if="chargeLoading" class="loading-tip">
        <i class="fa fa-spinner fa-spin mr-2"></i>
        <span>加载充值记录中...</span>
      </div>
      <div v-else-if="chargeError" class="error-tip">
        <i class="fa fa-exclamation-circle mr-1"></i>
        <span>获取充值记录失败：{{ chargeErrorMsg }}</span>
      </div>
      <div v-else-if="chargeRecords.length === 0 && !chargeLoading" class="empty-tip">
        暂无充值记录
      </div>
      <table v-else class="stats-table">
        <thead>
          <tr>
            <th>玩家ID</th>
            <th>玩家名称</th>
            <th>充值金额</th>
            <th>充值方式</th>
            <th>充值时间</th>
            <th>状态</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in chargeRecords" :key="item.id || item.orderId">
            <td>{{ item.playerId || '未知玩家' }}</td>
            <td>{{ item.playerName || '未知名称' }}</td>
            <td>¥{{ (item.amount || 0).toFixed(2) }}</td>
            <td>{{ item.payType || '未知方式' }}</td>
            <td>{{ item.payTime || new Date().toLocaleString() }}</td>
            <td>
              <span class="status-tag" :class="{ success: item.status === 'success', fail: item.status === 'fail' }">
                {{ item.status === 'success' ? '成功' : item.status === 'fail' ? '失败' : '处理中' }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 分页（仅掉落数据使用） -->
    <div v-if="activeTab === 'drop' && dropStats.length > 0" class="footer-bar">
      <p class="total-text">显示 {{ startIndex }} 至 {{ endIndex }} 条，共 {{ totalItems }} 条</p>
      <div class="pagination">
        <button 
          class="page-btn" 
          :disabled="currentPage === 1"
          @click="jumpToPage(1)"
        >
          <i class="fa fa-angle-double-left"></i>
        </button>
        <button 
          class="page-btn" 
          :disabled="currentPage === 1"
          @click="jumpToPage(currentPage - 1)"
        >
          <i class="fa fa-angle-left"></i>
        </button>
        <button class="page-btn active">{{ currentPage }}</button>
        <button 
          class="page-btn" 
          :disabled="currentPage === totalPages"
          @click="jumpToPage(currentPage + 1)"
        >
          <i class="fa fa-angle-right"></i>
        </button>
        <button 
          class="page-btn" 
          :disabled="currentPage === totalPages"
          @click="jumpToPage(totalPages)"
        >
          <i class="fa fa-angle-double-right"></i>
        </button>
      </div>
    </div>

    <!-- 充值记录分页 -->
    <div v-if="activeTab === 'charge' && chargeRecords.length > 0" class="footer-bar">
      <p class="total-text">显示 {{ chargeStartIndex }} 至 {{ chargeEndIndex }} 条，共 {{ chargeTotalItems }} 条</p>
      <div class="pagination">
        <button 
          class="page-btn" 
          :disabled="chargeCurrentPage === 1"
          @click="jumpToChargePage(1)"
        >
          <i class="fa fa-angle-double-left"></i>
        </button>
        <button 
          class="page-btn" 
          :disabled="chargeCurrentPage === 1"
          @click="jumpToChargePage(chargeCurrentPage - 1)"
        >
          <i class="fa fa-angle-left"></i>
        </button>
        <button class="page-btn active">{{ chargeCurrentPage }}</button>
        <button 
          class="page-btn" 
          :disabled="chargeCurrentPage === chargeTotalPages"
          @click="jumpToChargePage(chargeCurrentPage + 1)"
        >
          <i class="fa fa-angle-right"></i>
        </button>
        <button 
          class="page-btn" 
          :disabled="chargeCurrentPage === chargeTotalPages"
          @click="jumpToChargePage(chargeTotalPages)"
        >
          <i class="fa fa-angle-double-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onActivated } from 'vue';
import axios from 'axios';
import { ElMessage } from 'element-plus';

// Props定义
const props = defineProps({
  serverId: {
    type: String,
    required: true
  },
  selectedDate: {
    type: String,
    default: ''
  },
  loading: {
    type: Boolean,
    default: false
  }
});

// 基础响应式数据
const activeServerId = ref(props.serverId);
const playerName = ref('');
const activeTab = ref('drop');
const chargeKeyword = ref('');

// 掉落数据相关
const dropStats = ref([]);
const loading = ref(false);
const error = ref(false);
const currentPage = ref(1);
const pageSize = ref(10);
const totalItems = ref(0);
const totalPages = computed(() => Math.ceil(totalItems.value / pageSize.value));

// 充值记录相关
const chargeRecords = ref([]);
const chargeLoading = ref(false);
const chargeError = ref(false);
const chargeErrorMsg = ref('');
const chargeCurrentPage = ref(1);
const chargePageSize = ref(30);
const chargeTotalItems = ref(0);
const chargeTotalPages = computed(() => Math.ceil(chargeTotalItems.value / chargePageSize.value));

// =============== 核心：物品折叠/展开功能 ===============
const groupedDropStats = ref([]);
const expandedItems = ref(new Set());

// 按物品名称分组
const groupByItemName = (data) => {
  const groups = {};
  data.forEach(item => {
    const key = item.itemName || '未知物品';
    if (!groups[key]) {
      groups[key] = {
        itemName: key,
        totalCount: 0,
        items: []
      };
    }
    groups[key].items.push(item);
    groups[key].totalCount += item.count || 1;
  });
  return Object.values(groups);
};

// 切换展开/折叠
const toggleExpand = (itemName) => {
  if (expandedItems.value.has(itemName)) {
    expandedItems.value.delete(itemName);
  } else {
    expandedItems.value.add(itemName);
  }
};

// 数据变化自动分组
watch(dropStats, (newData) => {
  groupedDropStats.value = groupByItemName(newData);
  expandedItems.value.clear(); // 切换数据默认全部折叠
}, { immediate: true });
// ======================================================

// 分页计算
const startIndex = computed(() => {
  if (totalItems.value === 0) return 0;
  return (currentPage.value - 1) * pageSize.value + 1;
});
const endIndex = computed(() => {
  const end = currentPage.value * pageSize.value;
  return end > totalItems.value ? totalItems.value : end;
});

const chargeStartIndex = computed(() => {
  if (chargeTotalItems.value === 0) return 0;
  return (chargeCurrentPage.value - 1) * chargePageSize.value + 1;
});
const chargeEndIndex = computed(() => {
  const end = chargeCurrentPage.value * chargePageSize.value;
  return end > chargeTotalItems.value ? chargeTotalItems.value : end;
});

// 获取掉落数据
const fetchDropStats = async () => {
  if (!playerName.value.trim() || !activeServerId.value) {
    dropStats.value = [];
    totalItems.value = 0;
    ElMessage.warning(!playerName.value.trim() ? '请先输入玩家名称' : '请先选择区服');
    return;
  }

  loading.value = true;
  error.value = false;
  try {
    const response = await axios.get(`https://log-inter.wuxiahaojie.com/api/drop/BackData`, {
      params: {
        Name: playerName.value.trim(),
        serverId: activeServerId.value,
        time: props.selectedDate || '所有'
      }
    });
    const resData = response.data || {};
    dropStats.value = Array.isArray(resData.data) ? resData.data : [];
    totalItems.value = resData.total || dropStats.value.length;
  } catch (err) {
    console.error('获取掉落数据失败:', err);
    dropStats.value = [];
    totalItems.value = 0;
    error.value = true;
    ElMessage.error(`获取掉落数据失败：${err.message || '网络异常'}`);
  } finally {
    loading.value = false;
  }
};

// 加载充值记录
const loadChargeRecords = async () => {
  if (!activeServerId.value) {
    chargeRecords.value = [];
    chargeErrorMsg.value = '请先选择区服';
    chargeError.value = true;
    return;
  }

  chargeLoading.value = true;
  chargeError.value = false;
  chargeErrorMsg.value = '';
  
  const apiUrl = 'https://log-inter.wuxiahaojie.com/api/Recharge/GetRechargeRecords';
  
  try {
    const response = await axios.get(apiUrl, {
      params: {
        serverId: activeServerId.value,
        time: props.selectedDate || '所有',
        keyword: chargeKeyword.value.trim()
      },
      timeout: 5000
    });
    const resData = response.data || {};
    chargeRecords.value = Array.isArray(resData.records) ? resData.records : [];
    chargeTotalItems.value = Number(resData.totalCount) || 0;
  } catch (err) {
    console.error('获取充值记录失败：', err);
    chargeRecords.value = [];
    chargeTotalItems.value = 0;
    chargeError.value = true;
    chargeErrorMsg.value = err.message || '网络异常';
  } finally {
    chargeLoading.value = false;
  }
};

// 标签切换
const switchTab = (tabName) => {
  activeTab.value = tabName;
  if (tabName === 'drop') {
    if (playerName.value.trim()) fetchDropStats();
  } else if (tabName === 'charge') {
    chargeCurrentPage.value = 1;
    loadChargeRecords();
  }
};

// 分页跳转
const jumpToPage = (page) => {
  if (page < 1 || page > totalPages.value || page === currentPage.value) return;
  currentPage.value = page;
  fetchDropStats();
};

const jumpToChargePage = (page) => {
  if (page < 1 || page > chargeTotalPages.value || page === chargeCurrentPage.value) return;
  chargeCurrentPage.value = page;
  loadChargeRecords();
};

// 监听
watch(() => props.serverId, (newVal) => {
  activeServerId.value = newVal;
  currentPage.value = 1;
  chargeCurrentPage.value = 1;
  if (activeTab.value === 'drop' && playerName.value.trim()) {
    fetchDropStats();
  } else if (activeTab.value === 'charge') {
    loadChargeRecords();
  }
});

watch(() => props.selectedDate, (newDate) => {
  if (activeTab.value === 'drop') {
    currentPage.value = 1;
    if (playerName.value.trim()) fetchDropStats();
  } else if (activeTab.value === 'charge') {
    chargeCurrentPage.value = 1;
    loadChargeRecords();
  }
});

watch(playerName, () => {
  if (activeTab.value === 'drop') {
    currentPage.value = 1;
    if (playerName.value.trim()) fetchDropStats();
  }
});

watch(chargeKeyword, () => {
  if (activeTab.value === 'charge') {
    chargeCurrentPage.value = 1;
    loadChargeRecords();
  }
});

onMounted(() => {
  if (activeTab.value === 'charge') loadChargeRecords();
});

onActivated(() => {
  if (activeTab.value === 'drop' && playerName.value.trim()) {
    fetchDropStats();
  } else if (activeTab.value === 'charge') {
    loadChargeRecords();
  }
});
</script>

<style scoped>
/* 整体容器 */
.game-stats-container {
  background-color: #1a1a2e;
  border-radius: 4px;
  padding: 12px;
  color: #fff;
  font-size: 14px;
}

/* 标题 */
.stats-title {
  font-size: 16px;
  font-weight: bold;
  margin: 0 0 12px 0;
  color: #e0e0e0;
}

.date-info{
  margin-bottom: 10px;
  color: #ccc;
}

/* 输入框 */
.player-input-wrapper {
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.input-label {
  color: #ccc;
  font-size: 14px;
}
.player-input {
  background-color: #2d2d44;
  border: 1px solid #3d3d5c;
  border-radius: 4px;
  padding: 4px 8px;
  color: #fff;
  outline: none;
  min-width: 180px;
}
.player-input:focus {
  border-color: #3a7bd5;
}

/* 搜索框 */
.search-wrapper {
  position: relative;
  margin-bottom: 12px;
  width: 240px;
}
.search-input {
  width: 100%;
  background-color: #2d2d44;
  border: 1px solid #3d3d5c;
  border-radius: 4px;
  padding: 4px 8px;
  color: #fff;
  outline: none;
}
.search-input:focus {
  border-color: #3a7bd5;
}
.search-icon {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
  font-size: 12px;
}

/* 标签切换 */
.tab-switcher {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}
.tab-btn {
  background-color: #2d2d44;
  border: 1px solid #3d3d5c;
  border-radius: 4px;
  padding: 6px 16px;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s;
}
.tab-btn:hover {
  background-color: #353554;
}
.tab-btn.active {
  background-color: #3a7bd5;
  border-color: #3a7bd5;
}

/* 数据面板 */
.data-panel {
  margin-bottom: 12px;
}
.stats-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #3d3d5c;
  border-radius: 4px;
  overflow: hidden;
}
.stats-table th {
  background-color: #2d2d44;
  padding: 8px 12px;
  text-align: left;
  font-weight: normal;
  border-bottom: 1px solid #3d3d5c;
  color: #ccc;
}
.stats-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #3d3d5c;
  color: #e0e0e0;
}
.stats-table tr:last-child td {
  border-bottom: none;
}

/* =============== 折叠样式 =============== */
.group-row {
  background-color: #25253b;
  cursor: pointer;
  transition: background-color 0.2s;
}
.group-row:hover {
  background-color: #2d2d44;
}
.expand-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}
.indent-cell {
  padding-left: 24px !important;
}
.item-row {
  background-color: #1f1f32;
}
/* ======================================== */

/* 提示 */
.loading-tip {
  padding: 20px 0;
  text-align: center;
  color: #ccc;
  display: flex;
  justify-content: center;
  align-items: center;
}
.error-tip {
  padding: 20px 0;
  text-align: center;
  color: #ff4444;
}
.empty-tip {
  padding: 20px 0;
  text-align: center;
  color: #888;
}

/* 状态标签 */
.status-tag {
  padding: 2px 6px;
  border-radius: 2px;
  font-size: 12px;
}
.status-tag.success {
  background-color: #2e7d32;
  color: #fff;
}
.status-tag.fail {
  background-color: #c62828;
  color: #fff;
}

/* 分页栏 */
.footer-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #888;
  margin-top: 12px;
}
.total-text {
  margin: 0;
}
.pagination {
  display: flex;
  gap: 4px;
}
.page-btn {
  background-color: #2d2d44;
  border: 1px solid #3d3d5c;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  cursor: pointer;
  padding: 0;
  transition: all 0.2s;
}
.page-btn:hover:not(:disabled) {
  background-color: #353554;
  border-color: #4a4a70;
}
.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.page-btn.active {
  background-color: #3a7bd5;
  border-color: #3a7bd5;
}

.mr-1 { margin-right: 4px; }
.mr-2 { margin-right: 8px; }
</style>